import React, { useState, useEffect } from 'react';
import { useRegister, useToast } from '@/hooks';
import Button from '@/components/common/Button/Button';
import Input from '@/components/common/Input/Input';
import styles from './AuthForm.module.css';

/**
 * RegisterForm Component
 * Registration form with validation and toast notifications
 */
const RegisterForm = ({ onSwitchToSignin }) => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    country: '',
    referral: '',
    ruleChecked: false,
    marketingChecked: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  
  const {
    isLoading,
    showOtpSection,
    otp,
    setOtp,
    otpSent,
    otpVerified,
    resendTimer,
    sendOtp,
    verifyOtp,
    resendOtp,
    handleRegisterSubmit,
    showError,
    showInfo,
  } = useRegister();

  // OTP verification is skipped - registration proceeds directly

  const handleOtpSubmit = async (e) => {
    e?.preventDefault();
    await verifyOtp(formData.email, otp);
  };

  const handleResendOtp = async () => {
    await resendOtp(formData.email);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email is required
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password is required
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    } else {
      const strongPasswordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
      if (!strongPasswordRegex.test(formData.password)) {
        newErrors.password = 'Password must contain uppercase, lowercase, number, and special character';
      }
    }

    // Confirm password is required
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Username is optional, but if provided, validate it
    if (formData.username.trim()) {
      if (formData.username.length < 5) {
        newErrors.username = 'Username must be at least 5 characters';
      } else if (formData.username.length > 16) {
        newErrors.username = 'Username must be less than 16 characters';
      }
      
      // Check if username matches email local part
      const emailLocalPart = formData.email.split('@')[0];
      if (emailLocalPart.toLowerCase() === formData.username.toLowerCase()) {
        newErrors.username = 'Username and email name cannot be the same';
      }
    }

    // Terms checkbox is required
    if (!formData.ruleChecked) {
      newErrors.ruleChecked = 'You must agree to the Terms of Service';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      showError('Please fix the errors in the form');
      return;
    }

    await handleRegisterSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div className={styles.title}>Sign Up</div>
      <p className={styles.subtitle}>Create your account to get started.</p>

      <Input
        label="Username"
        type="text"
        name="username"
        value={formData.username}
        onChange={handleInputChange}
        placeholder="Enter username (5-16 characters)"
        error={errors.username}
        disabled={isLoading}
      />

      <Input
        label="Email"
        type="email"
        name="email"
        value={formData.email}
        onChange={handleInputChange}
        placeholder="Enter your email"
        error={errors.email}
        disabled={isLoading}
        required
      />

      <div className={styles.passwordInputWrapper}>
        <Input
          label="Password"
          type={showPassword ? 'text' : 'password'}
          name="password"
          value={formData.password}
          onChange={handleInputChange}
          placeholder="Enter password (min 8 characters)"
          error={errors.password}
          disabled={isLoading}
          required
        />
        <button
          type="button"
          className={styles.passwordToggle}
          onClick={() => setShowPassword(!showPassword)}
          disabled={isLoading}
          aria-label={showPassword ? 'Hide password' : 'Show password'}
        >
          {showPassword ? (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      </div>

      <div className={styles.passwordInputWrapper}>
        <Input
          label="Confirm Password"
          type={showConfirmPassword ? 'text' : 'password'}
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleInputChange}
          placeholder="Confirm your password"
          error={errors.confirmPassword}
          disabled={isLoading}
          required
        />
        <button
          type="button"
          className={styles.passwordToggle}
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          disabled={isLoading}
          aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
        >
          {showConfirmPassword ? (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          )}
        </button>
      </div>

      {/* <Input
        label="Phone Number (Optional)"
        type="tel"
        name="phoneNumber"
        value={formData.phoneNumber}
        onChange={handleInputChange}
        placeholder="Enter phone number"
        error={errors.phoneNumber}
        disabled={isLoading}
      /> */}
{/* 
      <Input
        label="Country (Optional)"
        type="text"
        name="country"
        value={formData.country}
        onChange={handleInputChange}
        placeholder="Enter country code (e.g., US, IN)"
        error={errors.country}
        disabled={isLoading}
      /> */}

      <Input
        label="Referral Code (Optional)"
        type="text"
        name="referral"
        value={formData.referral}
        onChange={handleInputChange}
        placeholder="Enter referral code"
        disabled={isLoading}
      />

      {showOtpSection && !otpVerified && (
        <div className={styles.otpSection}>
          <div className={styles.otpHeader}>
            <p>We've sent a verification code to your email</p>
            <button
              type="button"
              onClick={handleResendOtp}
              disabled={resendTimer > 0 || isLoading}
              className={styles.resendButton}
            >
              {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP'}
            </button>
          </div>
          <Input
            label="Verification Code"
            type="text"
            name="otp"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            placeholder="Enter verification code"
            disabled={isLoading}
            required
          />
          <Button
            type="button"
            variant="secondary"
            size="lg"
            fullWidth
            onClick={handleOtpSubmit}
            disabled={isLoading || !otp}
          >
            Verify OTP
          </Button>
        </div>
      )}

      <div className={styles.checkboxGroup}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            name="ruleChecked"
            checked={formData.ruleChecked}
            onChange={handleInputChange}
            className={styles.checkbox}
            disabled={isLoading}
          />
          <span>I agree to the{' '}
            <a href="/provably-fair" target="_blank" rel="noopener noreferrer">
              Terms of Service
            </a>
            {' '}and{' '}
            <a href="/provably-fair" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
          </span>
        </label>
        {errors.ruleChecked && (
          <span className={styles.errorText}>{errors.ruleChecked}</span>
        )}
      </div>

      <div className={styles.checkboxGroup}>
        <label className={styles.checkboxLabel}>
          <input
            type="checkbox"
            name="marketingChecked"
            checked={formData.marketingChecked}
            onChange={handleInputChange}
            className={styles.checkbox}
            disabled={isLoading}
          />
          <span>I agree to receive marketing communications</span>
        </label>
      </div>

      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        disabled={isLoading}
      >
        {isLoading ? 'Creating account...' : 'Sign Up'}
      </Button>

      <div className={styles.switchAuth}>
        Already have an account?{' '}
        <a href="#" onClick={(e) => { e.preventDefault(); onSwitchToSignin(); }}>
          Sign in
        </a>
      </div>
    </form>
  );
};

export default RegisterForm;
